import { FileText, Film, Loader2, TriangleAlert, X } from 'lucide-react';

import { Input } from '../../../../components/ui/input';
import { cn } from '../../../../lib/utils';
import { humanReadableSize } from '../../lib/compressMedia';
import { getFileBaseName, getFileExtension, type UploadRow } from '../../lib/uploadRows';

interface UploadPreviewRowProps {
  disabled: boolean;
  onEditName: (clientId: string, value: string) => void;
  onRemove: (clientId: string) => void;
  row: UploadRow;
}

const KindIcon = ({ kind }: { kind: UploadRow['kind'] }) => {
  if (kind === 'video') return <Film className="size-5 text-text-muted" />;
  return <FileText className="size-5 text-text-muted" />;
};

const STATUS_LABELS: Record<string, string> = {
  compressing: 'Comprimiendo…',
  naming: 'Analizando…',
  over_limit: 'Fuera de límite',
  error: 'Error',
};

const UploadPreviewRow = ({ disabled, onEditName, onRemove, row }: UploadPreviewRowProps) => {
  const isProcessing = row.status === 'compressing' || row.status === 'naming';
  const isReady = row.status === 'ready';
  const isOverLimit = row.status === 'over_limit';

  const suggestedBaseName = row.suggestedName ? getFileBaseName(row.suggestedName) : null;
  const inputValue = row.editedName ?? getFileBaseName(row.suggestedName ?? row.finalName);
  const extension = getFileExtension(row.finalName);

  const hasSuggestionToApply =
    isReady && suggestedBaseName !== null && row.editedName !== null && row.editedName !== suggestedBaseName;

  const sizeChanged = row.finalSize !== null && row.finalSize !== row.originalSize;

  return (
    <div className="flex items-start gap-3 border-b border-border-subtle py-3 last:border-b-0">
      <div className="flex size-12 flex-none items-center justify-center overflow-hidden rounded-lg bg-bg-surface-raised">
        {row.thumbnailUrl ? (
          <img alt="" className="size-full object-cover" src={row.thumbnailUrl} />
        ) : (
          <KindIcon kind={row.kind} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="mb-1 truncate text-xs text-text-muted line-through">{row.originalName}</p>

        {isProcessing ? (
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <Loader2 className="size-3 animate-spin" />
            {STATUS_LABELS[row.status]}
          </div>
        ) : (
          <div className="flex min-w-0">
            <Input
              className={cn(
                'h-7 min-w-0 flex-1 text-xs',
                extension && 'rounded-r-none border-r-0',
                isOverLimit && 'border-error/50'
              )}
              defaultValue={inputValue}
              disabled={disabled || isOverLimit}
              key={row.suggestedName ?? row.finalName}
              onBlur={(e) => onEditName(row.clientId, e.target.value)}
              placeholder={getFileBaseName(row.finalName)}
            />
            {extension && (
              <span
                className={cn(
                  'flex h-7 max-w-[40%] shrink-0 items-center truncate rounded-r-lg border border-l-0',
                  'border-input bg-bg-surface-raised px-2 text-xs text-text-muted'
                )}
              >
                .{extension}
              </span>
            )}
          </div>
        )}

        {hasSuggestionToApply && (
          <button
            className="mt-1 text-xs text-brand-vivid hover:underline"
            onClick={() => onEditName(row.clientId, suggestedBaseName)}
            type="button"
          >
            ← IA: {row.suggestedName}
          </button>
        )}

        {(isOverLimit || row.status === 'error') && row.errorMessage && (
          <p className="mt-1 flex items-center gap-1 text-xs text-error">
            <TriangleAlert className="size-3" />
            {row.errorMessage}
          </p>
        )}
      </div>

      <div className="flex-none text-right">
        {sizeChanged ? (
          <div className="flex flex-col items-end">
            <span className="text-xs text-text-muted line-through">{humanReadableSize(row.originalSize)}</span>
            <span className="text-xs text-success">{humanReadableSize(row.finalSize!)}</span>
          </div>
        ) : (
          <span className="text-xs text-text-muted">{humanReadableSize(row.originalSize)}</span>
        )}
      </div>

      <button
        aria-label={`Quitar ${row.originalName}`}
        className="flex-none text-text-muted hover:text-error disabled:opacity-30"
        disabled={disabled}
        onClick={() => onRemove(row.clientId)}
        type="button"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};

export default UploadPreviewRow;
